import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import LoaderOverlay from 'src/components/Loader';
import { getFallbackUrl } from 'src/features/specializedStores/domain/config/specializedStores.config';

interface AuthProtectedRouteProps {
  children: React.ReactNode;
  expectedAuthState?: 'authenticated' | 'non-authenticated';
}

function AuthProtectedRoute({
  children,
  expectedAuthState = 'authenticated',
}: AuthProtectedRouteProps) {
  const { session, user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (expectedAuthState === 'non-authenticated') {
      if (session && user) {
        const fallbackUrl = getFallbackUrl({ isAuthenticated: true });
        navigate(fallbackUrl, { replace: true });
        setIsAllowed(false);
        return;
      }
      setIsAllowed(true);
      return;
    }

    if (expectedAuthState === 'authenticated') {
      if (!session || !user) {
        const fallbackUrl = getFallbackUrl({ isAuthenticated: false });
        navigate(fallbackUrl, { replace: true });
        setIsAllowed(false);
        return;
      }
      setIsAllowed(true);
      return;
    }
  }, [location.pathname, navigate, session, user, expectedAuthState]);

  if (isAllowed === null) {
    return <LoaderOverlay />;
  }

  return isAllowed ? children : null;
}

export default AuthProtectedRoute;
